import { useState } from 'react'
import { Link } from 'react-router-dom'

import MaculMap from '../../images/privacion_macul_2024.png'

import './gallery.css'


const Gallery = () => {

  const [isMapOpen, setIsMapOpen] = useState(false)


  return (
    <main className="findings-page">

      {/* =====================================================
          HERO
          ===================================================== */}

      <section className="findings-hero">

        <div className="findings-hero__inner">

          <p className="findings-eyebrow">
            HALLAZGOS
          </p>

          <h1>
            Datos que revelan
            <span> cómo cambia el territorio.</span>
          </h1>

          <p className="findings-hero__lead">
            Análisis y visualizaciones construidos a partir de
            fuentes oficiales para explorar desigualdad,
            privación social, demografía y territorio.
          </p>

          <div className="findings-hero__meta">

            <span>Datos oficiales</span>
            <span>Análisis territorial</span>
            <span>Visualización</span>
            <span>Evidencia aplicada</span>

          </div>

        </div>

      </section>


      {/* =====================================================
          INTRO
          ===================================================== */}

      <section className="findings-intro">

        <div className="findings-intro__number">
          01
        </div>

        <div className="findings-intro__content">

          <p className="findings-eyebrow">
            DESIGUALDAD TERRITORIAL
          </p>

          <h2>
            Una comuna no es
            un territorio homogéneo.
          </h2>

          <p>
            Los indicadores agregados permiten comparar comunas,
            pero pueden ocultar diferencias importantes que ocurren
            dentro de ellas.
          </p>

          <p>
            El análisis a escala infracomunal permite identificar
            patrones espaciales, concentraciones y áreas que requieren
            una observación mucho más detallada.
          </p>

        </div>

      </section>


      {/* =====================================================
          HALLAZGO 01
          ===================================================== */}

      <section className="finding">

        <div className="finding__header">

          <div>
            <p className="findings-eyebrow">
              HALLAZGO 01 · MACUL
            </p>

            <h2>
              La privación social no se distribuye
              uniformemente dentro de Macul.
            </h2>
          </div>

          <div className="finding__index">
            01
          </div>

        </div>


        <div className="finding__main">

          {/* MAPA */}

          <div className="finding__visual">

            <button
              type="button"
              className="finding__image-button"
              onClick={() => setIsMapOpen(true)}
              aria-label="Ampliar mapa de privación social en Macul"
            >
              <img
                src={MaculMap}
                alt="Mapa de privación social y focos de concentración en la comuna de Macul"
              />

              <span className="finding__zoom">
                Ampliar mapa
              </span>
            </button>

          </div>


          {/* INTERPRETACIÓN */}

          <div className="finding__analysis">

            <p className="finding__label">
              LECTURA DEL MAPA
            </p>

            <h3>
              La escala comunal puede esconder
              diferencias territoriales relevantes.
            </h3>

            <p>
              El mapa muestra la distribución espacial de la
              privación social al interior de la comuna de Macul,
              utilizando información del Censo 2024.
            </p>

            <p>
              La intensidad cromática permite observar diferencias
              entre unidades territoriales pequeñas, mientras que
              los puntos numerados identifican focos de concentración
              de la privación.
            </p>

            <p>
              La visualización permite pasar de una lectura basada
              exclusivamente en promedios comunales a una mirada
              infracomunal, donde aparecen patrones que pueden quedar
              ocultos al trabajar únicamente con indicadores agregados.
            </p>


            <div className="finding__key">

              <div>
                <span className="finding__key-dot finding__key-dot--light"></span>

                <p>
                  Menor intensidad
                </p>
              </div>

              <div>
                <span className="finding__key-dot finding__key-dot--dark"></span>

                <p>
                  Mayor intensidad
                </p>
              </div>

              <div>
                <span className="finding__focus-dot">
                  1
                </span>

                <p>
                  Foco de concentración
                </p>
              </div>

            </div>

          </div>

        </div>


        {/* =================================================
            FICHA
            ================================================= */}

        <div className="finding__metadata">

          <div>
            <span>FUENTE</span>
            <strong>Censo 2024</strong>
          </div>

          <div>
            <span>TERRITORIO</span>
            <strong>Macul</strong>
          </div>

          <div>
            <span>ESCALA</span>
            <strong>Infracomunal</strong>
          </div>

          <div>
            <span>ANÁLISIS</span>
            <strong>Privación social</strong>
          </div>

          <div>
            <span>RESULTADO</span>
            <strong>Focos de concentración</strong>
          </div>

        </div>

      </section>


      {/* =====================================================
          PRINCIPIO ANALÍTICO
          ===================================================== */}

      <section className="findings-principle">

        <div className="findings-principle__content">

          <p className="findings-eyebrow">
            POR QUÉ IMPORTA
          </p>

          <h2>
            El promedio de una comuna
            no cuenta toda la historia.
          </h2>

          <p>
            Dos territorios pertenecientes a una misma comuna pueden
            presentar condiciones sociales muy diferentes. Incorporar
            la dimensión espacial permite localizar esas diferencias
            y construir diagnósticos territorialmente más precisos.
          </p>

        </div>


        <div className="findings-principle__diagram">

          <div>
            <span>01</span>
            <strong>Medir</strong>
            <p>Construir indicadores.</p>
          </div>

          <div>
            <span>02</span>
            <strong>Localizar</strong>
            <p>Observar su distribución.</p>
          </div>

          <div>
            <span>03</span>
            <strong>Identificar</strong>
            <p>Detectar concentraciones.</p>
          </div>

          <div>
            <span>04</span>
            <strong>Interpretar</strong>
            <p>Comprender el territorio.</p>
          </div>

        </div>

      </section>


      {/* =====================================================
          FUTUROS HALLAZGOS
          ===================================================== */}

      <section className="findings-more">

        <div className="findings-more__heading">

          <p className="findings-eyebrow">
            EXPLORAR
          </p>

          <h2>
            Más dimensiones del territorio.
          </h2>

          <p>
            Esta sección irá incorporando nuevos análisis construidos
            a partir de información demográfica y social.
          </p>

        </div>


        <div className="findings-topics">

          <article>
            <span>01</span>
            <h3>Desigualdad</h3>
            <p>
              Distribución y concentración de recursos.
            </p>
          </article>

          <article>
            <span>02</span>
            <h3>Pobreza</h3>
            <p>
              Diferencias sociales y territoriales.
            </p>
          </article>

          <article>
            <span>03</span>
            <h3>Demografía</h3>
            <p>
              Estructura y transformación de la población.
            </p>
          </article>

          <article>
            <span>04</span>
            <h3>Territorio</h3>
            <p>
              Patrones espaciales a escala local.
            </p>
          </article>

        </div>

      </section>


      {/* =====================================================
          CENSO CTA
          ===================================================== */}

      <section className="findings-censo">

        <div>

          <p className="findings-eyebrow">
            EXPLORA LOS DATOS
          </p>

          <h2>
            Del hallazgo a la exploración territorial.
          </h2>

          <p>
            Explora indicadores, mapas y escenarios territoriales
            en la plataforma Censo 2024 de Demográfica.
          </p>

        </div>

        <Link
          to="/censo"
          className="findings-btn"
        >
          Explorar Censo 2024 →
        </Link>

      </section>


      {/* =====================================================
          MODAL MAPA
          ===================================================== */}

      {isMapOpen && (

        <div
          className="finding-modal"
          onClick={() => setIsMapOpen(false)}
        >

          <div
            className="finding-modal__content"
            onClick={(event) => event.stopPropagation()}
          >

            <button
              type="button"
              className="finding-modal__close"
              onClick={() => setIsMapOpen(false)}
              aria-label="Cerrar mapa"
            >
              ×
            </button>

            <img
              src={MaculMap}
              alt="Mapa ampliado de privación social en Macul"
            />

          </div>

        </div>

      )}

    </main>
  )
}


export default Gallery